/**
 * CatalogProvider: single source of truth for marketplace catalog data.
 *
 * Owns THREE independent fetches, exposes plugins, loading/error state, and catalog
 * utilities (findByInstallKey, parentIndex, removePlugin) via context. Must be
 * rendered inside AuthGuard so the fetches are gated by authentication.
 *
 * The surprising property, and the point of the design: only the CATALOG fetch feeds
 * isLoading and error. The other two — adoption.json (Contract 7) and vote tallies
 * (Contract 8) — sit outside both, because each resolves to null rather than
 * rejecting, so neither can delay or fail a catalog load. `plugins` is the read-time
 * join of all three and settles up to three times per load; see mergeAdoption on why
 * array identity is preserved when nothing matched.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { AdoptionMeta, MarketplaceEntry, MarketplaceClientConfig } from "../types/index";
import type { AdoptionCatalog } from "@skills-marketplace/contracts/contract-7-adoption";
import type { FeedbackTally, TalliesResponse } from "@skills-marketplace/contracts/contract-8-feedback";
import { adoptionUrlFor, createMarketplaceClient, fetchAdoption } from "./marketplace-client";
import { fetchFeedbackTallies } from "./feedback-client";
import { buildParentIndex } from "./parent-plugin";
import { reportError } from "./newrelic";

// ── Install-key helpers ────────────────────────────────────────────────────────
// Moved from App.tsx so the provider and all consumers share one definition.

export function installKeyForPlugin(plugin: MarketplaceEntry): string {
  return (
    plugin.installName ??
    `${plugin.source}--${plugin.orgOrOwner.toLowerCase()}--${plugin.repoName.toLowerCase()}`
  );
}

export function findPluginByInstallKey(
  catalog: MarketplaceEntry[],
  installKey: string,
): MarketplaceEntry | null {
  return (
    catalog.find(
      (p) => installKeyForPlugin(p).toLowerCase() === installKey.toLowerCase(),
    ) ?? null
  );
}

// ── Adoption join ──────────────────────────────────────────────────────────────

/**
 * Attach adoption figures to catalog entries by direct `installName` lookup.
 *
 * The artifact is keyed by installName, which the catalog builder guarantees to
 * be unique per entry (`dedupeByInstallName`). So this reconstructs NO key:
 * neither `installKeyForPlugin`'s source/org/repo fallback nor
 * `officialIdentityKey()` distinguishes synthesized per-skill entries — they all
 * collapse onto their parent plugin's identity, which would smear one plugin's
 * figure across every one of its skills. An entry with no installName is simply
 * unmeasurable.
 *
 * Pure and total: an entry with no matching figure is returned untouched (no
 * `adoption` key at all), which is the single empty state — there is no
 * "measured zero". See contracts/contract-7-adoption.ts.
 */
export function mergeAdoption(
  plugins: MarketplaceEntry[],
  adoption: AdoptionCatalog | null,
): MarketplaceEntry[] {
  if (!adoption) return plugins;

  let matched = 0;
  const merged = plugins.map((entry) => {
    // hasOwnProperty, not a bare index read: entries is null-prototype when it comes
    // from validateAdoption, but this function is exported and typed against a plain
    // AdoptionCatalog, so it must not depend on its caller for that. installNames are
    // free-form slugs and one can legitimately be "constructor" or "toString".
    const figure =
      entry.installName && Object.prototype.hasOwnProperty.call(adoption.entries, entry.installName)
        ? adoption.entries[entry.installName]
        : undefined;
    if (!figure) return entry;
    matched++;
    return { ...entry, adoption: figure };
  });

  // Preserving array identity keeps downstream memos from rebuilding over a
  // 1500-entry catalog for no reason — search.ts caches its MiniSearch index in a
  // WeakMap keyed on this array reference.
  //
  // Note this guard is all-or-nothing across the whole array, not per-entry, and
  // `adoption === null` already returned above — so with ~42% coverage it does NOT
  // fire on the happy path. It fires when a non-null artifact overlaps nothing: a
  // preview pointed at another environment's artifact, or a stale key scheme.
  return matched > 0 ? merged : plugins;
}

// ── Feedback join ──────────────────────────────────────────────────────────────

/**
 * Attach community vote tallies to catalog entries by direct `installName` lookup
 * (Contract 8, #617).
 *
 * Same key, same reasoning, same array-identity discipline as mergeAdoption above —
 * see that function's notes on why no key is reconstructed and why the all-or-nothing
 * identity guard matters for the MiniSearch cache.
 *
 * Kept as a separate pass rather than folded into mergeAdoption because the two
 * sources arrive independently: adoption is a published artifact on a 3–4 day lag,
 * tallies come live from an API. Either can be null without affecting the other.
 */
export function mergeFeedback(
  plugins: MarketplaceEntry[],
  tallies: TalliesResponse | null,
): MarketplaceEntry[] {
  if (!tallies) return plugins;

  let matched = 0;
  const merged = plugins.map((entry) => {
    // hasOwnProperty rather than a bare index read, for the reason mergeAdoption
    // documents: installNames are free-form slugs and one can legitimately be
    // "constructor" or "toString".
    const tally =
      entry.installName && Object.prototype.hasOwnProperty.call(tallies.entries, entry.installName)
        ? tallies.entries[entry.installName]
        : undefined;
    if (!tally) return entry;
    matched++;
    return { ...entry, feedback: tally };
  });

  return matched > 0 ? merged : plugins;
}

// ── Context ────────────────────────────────────────────────────────────────────

interface CatalogContextValue {
  plugins: MarketplaceEntry[];
  isLoading: boolean;
  error: string | null;
  /**
   * Force a cache-busting re-fetch. Pass `{ silent: true }` for a best-effort
   * background refetch (e.g. the post-publish "View in Catalog" refresh) that must
   * NOT flip the shared `error` state on failure; the caller has its own fallback
   * UI, and poisoning the global error would wrongly claim the catalog is down
   * (and blank the catalog page) after an otherwise-successful action. The failure
   * is still reported to New Relic. Default (non-silent) sets `error` on failure,
   * for user-initiated retries that want the error surfaced.
   *
   * Resolves to whether the refetch succeeded, so a silent caller (e.g. the manual
   * "Refresh" button in the "processing" detail state) can show scoped feedback on
   * failure without touching the shared `error`.
   */
  refresh: (opts?: { silent?: boolean }) => Promise<boolean>;
  /** Optimistically remove a plugin by install key (used after a successful delete). */
  removePlugin: (installKey: string) => void;
  /** Look up a plugin by its install key. Returns null when not found. */
  findByInstallKey: (installKey: string) => MarketplaceEntry | null;
  /** Inverted skill→parent index built from the catalog. Key = child installName. */
  parentIndex: Map<string, MarketplaceEntry>;
  /**
   * Record the tally a vote write returned, so every surface reflects the voter's
   * own change without a refetch and without computing a tally client-side (#617).
   */
  applyVoteResult: (installName: string, tally: FeedbackTally) => void;
  /** Envelope of the joined adoption artifact ("as of" date, window). Null when unavailable. */
  adoptionMeta: AdoptionMeta | null;
}

const CatalogContext = createContext<CatalogContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────────

export function CatalogProvider({
  children,
  config,
}: {
  children: ReactNode;
  /** Marketplace client config. Pass a stable (memoized) object;
   *  changing it rebuilds the client and re-fetches the catalog. */
  config: Partial<MarketplaceClientConfig>;
}) {
  const [rawPlugins, setRawPlugins] = useState<MarketplaceEntry[]>([]);
  const [adoption, setAdoption] = useState<AdoptionCatalog | null>(null);
  const [feedback, setFeedback] = useState<TalliesResponse | null>(null);

  /**
   * installNames whose tally came from the VOTER'S OWN write rather than a bulk read.
   *
   * A ref, not state: nothing renders from it, and it must be readable by the merge
   * below without adding a dependency that would re-run the fetch.
   */
  const locallyVotedRef = useRef<Set<string>>(new Set());

  /**
   * Apply a bulk tallies snapshot WITHOUT clobbering the voter's own writes.
   *
   * Two ways a blind `setFeedback(result)` loses a vote that was already cast:
   *
   *   1. The snapshot lands late. marketplace.json is a static CDN file while
   *      /feedback/tallies is a Lambda plus a DynamoDB read, so the dynamic one is
   *      routinely last. Someone who deep-links to a detail page and votes before it
   *      returns gets a snapshot taken BEFORE their vote, and the count they were just
   *      shown slides back to the old number. Their "You marked this useful" and Undo
   *      survive (separate state), which makes it worse: the page contradicts itself.
   *   2. The snapshot FAILS. fetchFeedbackTallies resolves to null, and assigning that
   *      would drop their applied vote along with everything else.
   *
   * `prev ?? incoming` cannot fix this — it would discard every OTHER entry's bulk
   * tally the moment any vote landed. So the incoming map wins everywhere except the
   * entries this session voted on, which keep the authoritative counts their own write
   * returned. Null is ignored outright, which is also what makes a failed refresh
   * unable to demote good data (the same rule adoption follows).
   */
  const mergeTallies = useCallback((incoming: TalliesResponse | null) => {
    // A null read is not a snapshot, so it consumes nothing: the override below must
    // still be waiting for the next REAL one.
    if (!incoming) return;
    // Consumed here, outside the updater, for two separate reasons.
    //
    // EXPIRY. The override exists for exactly one window: a snapshot that was already
    // in flight when the vote landed, and therefore predates it. Any snapshot after
    // this one was issued later than the vote and already contains it. Holding the
    // override for the rest of the session would pin the voter's own figure forever and
    // hide every LATER vote by anyone else on that entry from every future refresh --
    // trading a race for a permanent staleness bug, in the one file that promises
    // "three independent fetches settle up to three times per load". Raised in review
    // on #571 (Jordan-Thomas).
    //
    // PURITY. React may invoke a state updater more than once for a single update
    // (StrictMode does exactly this in dev), so clearing the ref inside it would let a
    // second invocation read an already-emptied set and silently skip the protection.
    // Reading and clearing before the updater keeps it a pure function of `prev`.
    const overrides = [...locallyVotedRef.current];
    locallyVotedRef.current = new Set();
    setFeedback((prev) => {
      if (!prev || overrides.length === 0) return incoming;
      const entries = { ...incoming.entries };
      for (const installName of overrides) {
        const mine = prev.entries[installName];
        if (mine) entries[installName] = mine;
      }
      return { ...incoming, entries };
    });
  }, []);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const client = useMemo(() => createMarketplaceClient(config), [config]);

  // Adoption lives in a separate artifact next to marketplace.json. Resolved from
  // the same config so a caller overriding catalogUrl (previews, tests) gets the
  // matching sibling.
  const adoptionConfig = useMemo(
    () => ({
      // adoptionUrlDefault, not adoptionUrl: this is a derivation, so it must not
      // outrank an explicitly-set REACT_APP_ADOPTION_URL.
      adoptionUrlDefault: config.catalogUrl ? adoptionUrlFor(config.catalogUrl) : undefined,
      getToken: config.getToken,
    }),
    [config.catalogUrl, config.getToken],
  );

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    client
      .getCatalog()
      .then((catalog) => {
        if (cancelled) return;
        setRawPlugins(catalog.plugins);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        reportError(err, "catalog-fetch-routing");
        setError(err instanceof Error ? err.message : "Failed to load catalog");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [client]);

  // Adoption is fetched independently of the catalog, never awaited alongside it:
  // a slow or missing artifact must not delay — or fail — the catalog render. It
  // is also deliberately outside isLoading/error, since fetchAdoption resolves to
  // null instead of rejecting.
  useEffect(() => {
    let cancelled = false;

    void fetchAdoption(adoptionConfig).then((result) => {
      if (!cancelled) setAdoption(result);
    });

    return () => {
      cancelled = true;
    };
  }, [adoptionConfig]);

  // Vote tallies, on the same terms as adoption and for the same reason: they are
  // decoration on a catalog that must render without them, and fetchFeedbackTallies
  // resolves to null rather than rejecting. Outside isLoading/error so a missing or
  // slow feedback API can neither delay nor fail a catalog load.
  //
  // Runs once per mount: the tallies URL comes from env and the shared API base,
  // neither of which is per-provider config, and mergeTallies is stable.
  //
  // Goes through mergeTallies rather than setFeedback so a snapshot that lands AFTER
  // a vote cannot revert it -- see mergeTallies for why this is the likely order.
  useEffect(() => {
    let cancelled = false;

    void fetchFeedbackTallies().then((result) => {
      if (!cancelled) mergeTallies(result);
    });

    return () => {
      cancelled = true;
    };
  }, [mergeTallies]);

  const refresh = useCallback(async (opts?: { silent?: boolean }): Promise<boolean> => {
    const silent = opts?.silent ?? false;
    setIsLoading(true);
    if (!silent) setError(null);
    // Kick off the adoption re-fetch first so it overlaps the catalog fetch; it
    // resolves to null on failure, so there is nothing here to catch.
    const adoptionPromise = fetchAdoption(adoptionConfig);
    // Tallies are re-fetched on the same terms. Without this, refresh() re-synced the
    // catalog and adoption but left the tally map frozen at mount -- contradicting this
    // file's own "THREE independent fetches" claim, and leaving a caller like the
    // post-publish "View in Catalog" silent refresh showing stale counts for the rest
    // of the session.
    const feedbackPromise = fetchFeedbackTallies();
    let ok: boolean;
    try {
      const catalog = await client.refreshCatalog();
      setRawPlugins(catalog.plugins);
      // A successful fetch means the catalog is healthy: clear any stale error. The
      // silent path skips the optimistic clear above, so without this a prior error
      // (e.g. a failed initial load) would survive a good refetch and mask the
      // caller's fallback UI (the "processing" detail state) with "catalog is down".
      setError(null);
      ok = true;
    } catch (err: unknown) {
      reportError(err, "catalog-refresh");
      // A silent refetch must not flip the shared error state; the caller's own
      // fallback UI (e.g. the "processing" detail state) stays in place, and the
      // catalog page isn't blanked over a best-effort background fetch. The boolean
      // return lets that caller show its own scoped failure feedback instead.
      if (!silent) setError(err instanceof Error ? err.message : "Failed to refresh catalog");
      ok = false;
    } finally {
      setIsLoading(false);
    }
    // A refresh must not DEMOTE good adoption data. fetchAdoption collapses every
    // failure into null, so a transient blip would otherwise replace real figures
    // with the empty state — which reads as "no usage yet", an indistinguishable
    // wrong answer rather than a visible error. The mount effect above still sets
    // null, because there it genuinely is the first read.
    const nextAdoption = await adoptionPromise;
    if (nextAdoption) setAdoption(nextAdoption);
    // mergeTallies applies the same two protections here: a null (failed) read is
    // ignored rather than demoting good counts, and any entry voted on during this
    // session keeps the authoritative tally its own write returned.
    mergeTallies(await feedbackPromise);
    return ok;
  }, [client, adoptionConfig, mergeTallies]);

  const removePlugin = useCallback((installKey: string) => {
    setRawPlugins((prev) =>
      prev.filter((p) => installKeyForPlugin(p).toLowerCase() !== installKey.toLowerCase()),
    );
  }, []);

  /**
   * Record the authoritative tally a vote write returned.
   *
   * The POST/DELETE response carries the entry's new counts, so the voter's own
   * change is reflected everywhere it appears — the card badge and the detail block —
   * without a refetch, and without any surface computing a tally itself. Merging it
   * into the shared map is what keeps those surfaces agreeing, which #262 established as
   * the property to protect (a figure shown in three places from one source can only
   * diverge through a rendering bug).
   *
   * Tolerates a null map: the voter may be the first person to vote in a session
   * where the tallies read failed, and their own vote should still show.
   */
  const applyVoteResult = useCallback((installName: string, tally: FeedbackTally) => {
    // Claim this entry before the state update: from here on, a bulk snapshot must not
    // overwrite it, however late that snapshot arrives. See mergeTallies.
    locallyVotedRef.current.add(installName);
    setFeedback((prev) => ({
      // Tracks TalliesResponse's literal type, so a contract bump is a compile error
      // here rather than a silent mismatch. Only used when `prev` is null (the voter
      // is first in a session whose tallies read failed) — a real response always
      // supplies its own version, and fetchFeedbackTallies accepts any 1.x.
      schemaVersion: prev?.schemaVersion ?? "1.1",
      generatedAt: prev?.generatedAt ?? new Date().toISOString(),
      entries: { ...(prev?.entries ?? {}), [installName]: tally },
    }));
  }, []);

  // The read-time joins, chained. Everything downstream consumes the merged list, so
  // no consumer needs to know adoption and tallies arrived on separate requests — or
  // that either might be absent.
  const plugins = useMemo(
    () => mergeFeedback(mergeAdoption(rawPlugins, adoption), feedback),
    [rawPlugins, adoption, feedback],
  );

  const findByInstallKey = useCallback(
    (installKey: string) => findPluginByInstallKey(plugins, installKey),
    [plugins],
  );

  const parentIndex = useMemo(() => buildParentIndex(plugins), [plugins]);

  const adoptionMeta = useMemo<AdoptionMeta | null>(() => {
    if (!adoption) return null;
    const { entries: _entries, ...meta } = adoption;
    return meta;
  }, [adoption]);

  const value = useMemo<CatalogContextValue>(
    () => ({ plugins, isLoading, error, refresh, removePlugin, findByInstallKey, parentIndex, adoptionMeta, applyVoteResult }),
    [plugins, isLoading, error, refresh, removePlugin, findByInstallKey, parentIndex, adoptionMeta, applyVoteResult],
  );

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useCatalog(): CatalogContextValue {
  const ctx = useContext(CatalogContext);
  if (!ctx) {
    throw new Error("useCatalog must be used within a CatalogProvider");
  }
  return ctx;
}
